/**
 * Block-edit reducer helpers for Phase 2.3 (BEDIT / BSAVE / BCLOSE).
 *
 * Extracted from geometryReducer.ts to keep that file under the 2000-LOC
 * CLAUDE.md soft limit. Public surface:
 *
 *   - reduceEnterBlockEdit(document, blockName, createIfMissing)
 *   - reduceExitBlockEdit(document, action)
 *   - reduceSaveBlockEdit(document)
 *   - syncBlockEditSpaceToDefinition(document)     -- bidirectional mirror
 *
 * Architectural model (see also packages/drawing-model/src/helpers.ts):
 *
 * The block-edit space is a transient in-memory space with a fixed id
 * (BLOCK_EDIT_SPACE_ID). Its `entities` array mirrors the target
 * BlockDefinition's `entities` array while the session is active. The
 * mirror runs in two directions:
 *
 *   enter:  blockDef.entities -> space.entities  (reparent spaceId)
 *   every
 *   edit:   space.entities    -> blockDef.entities  (keep them in sync)
 *   exit:   (save)  keep current blockDef
 *           (disc.) restore blockDef from snapshot
 *
 * This means every geometry/* command that already iterates
 * `document.spaces[*].entities` continues to work unchanged inside BEDIT
 * — the usual "append / map / filter" loops touch the block-edit space
 * like any other space. The only extra work is a `syncBlockEditSpaceToDefinition`
 * pass run by the reducer right after the geometry command executes to
 * push the latest space.entities state back into blockDef.entities. If
 * no block-edit session is active, the helper is a no-op.
 */

import {
  BLOCK_EDIT_SPACE_ID,
  addToRefeditWorkingSet,
  assembleDrawingDocument,
  blockChildEntitySchema,
  blockDefinitionSchema,
  enterBlockEditSpace,
  enterBlockTestSpace,
  enterRefeditSpace,
  exitBlockEditSpace,
  exitBlockTestSpace,
  exitRefeditSpace,
  refreshBlockEditSnapshot,
  removeFromRefeditWorkingSet,
  type BlockChildEntity,
  type DrawingDocument,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

/**
 * Mirror `blocks[blockEditName].entities` into the block-edit space's
 * `entities` array. Called on enter to populate the synthetic space.
 * Entities coming out of blockDef have no spaceId — we reparent them
 * to BLOCK_EDIT_SPACE_ID so validateDrawingModel is happy and the
 * shell layer treats them as ordinary space-scoped entities.
 */
function hydrateBlockEditSpace(document: DrawingDocument): DrawingDocument {
  const blockEdit = document.spaces.find((s) => s.kind === "block-edit");
  if (!blockEdit || !blockEdit.blockEditName) {
    return document;
  }
  const blockDef = document.blocks[blockEdit.blockEditName];
  if (!blockDef) {
    return document;
  }

  // Reparent blockDef.entities to the block-edit space id so the
  // reducer's common patterns (filter-by-space, map-entities) pick them
  // up like any other space entities. Generate fresh ids if any of them
  // collide with entities already in other spaces (never should, but
  // defensive).
  const otherEntityIds = new Set<string>();
  for (const space of document.spaces) {
    if (space.kind === "block-edit") continue;
    for (const entity of space.entities) otherEntityIds.add(entity.id);
  }

  const reparented: DrawingEntity[] = blockDef.entities
    .filter(
      // ATTDEF lives in block definitions but is NOT part of the
      // runtime drawing-entity union (attdefEntitySchema is separate).
      // For Phase 2.3 we drop ATTDEFs from the block-edit canvas — the
      // ATTDEF authoring UI (Phase 2.1) is out of scope for this
      // subagent's task. The snapshot round-trip still preserves them.
      (child): child is Exclude<BlockChildEntity, { type: "Attdef" }> =>
        child.type !== "Attdef",
    )
    .map((child) => {
      // Reparent to the synthetic block-edit space.
      return { ...child, spaceId: BLOCK_EDIT_SPACE_ID } as DrawingEntity;
    });

  return assembleDrawingDocument({
    ...document,
    spaces: document.spaces.map((space) =>
      space.kind === "block-edit" ? { ...space, entities: reparented } : space,
    ),
  });
}

/**
 * Mirror the block-edit space's `entities` back into
 * `blocks[blockEditName].entities`. Called after every geometry
 * reducer pass so BSAVE always has a fresh picture of the authored
 * geometry. No-op when no block-edit session is active.
 *
 * When no block-edit session is active, or when the targeted
 * BlockDefinition no longer exists (e.g. another reducer code path
 * removed it — currently impossible, but defensive), we return the
 * document unchanged.
 *
 * ATTDEF children present in the original BlockDefinition are
 * preserved verbatim (the block-edit canvas strips them on hydrate;
 * we re-merge them on sync so BEDIT does not accidentally destroy
 * attribute definitions).
 */
export function syncBlockEditSpaceToDefinition(
  document: DrawingDocument,
): DrawingDocument {
  const blockEdit = document.spaces.find((s) => s.kind === "block-edit");
  if (!blockEdit || !blockEdit.blockEditName) return document;

  const blockName = blockEdit.blockEditName;
  const existing = document.blocks[blockName];
  if (!existing) return document;

  // Existing ATTDEFs survive across BEDIT — they are invisible to the
  // drawing canvas but must stay in the persistent BlockDefinition.
  const preservedAttdefs = existing.entities.filter((e) => e.type === "Attdef");

  // Re-parent block-edit space entities back to a "spaceId-less" child
  // form and validate via blockChildEntitySchema so we reject anything
  // outside the blockChild subset. As of Batch-7 (S-BLOCKCHILD-*) that
  // subset includes Hatch / Dimension / Multileader / Ellipse / Spline —
  // all valid block-definition children per the Autodesk DXF reference —
  // so they survive the sync. Genuinely non-block-child types (e.g.
  // Viewport / Wipeout, absent from the union) are still dropped.
  const authored: BlockChildEntity[] = [];
  for (const entity of blockEdit.entities) {
    // Clear the synthetic spaceId so the child schema is satisfied.
    // blockChildEntitySchema extends drawingEntityBaseSchema which
    // still has spaceId (min 1) — we keep it but store BLOCK_EDIT_SPACE_ID
    // for internal consistency. On exit / persist the DXF writer ignores
    // the spaceId on block children entirely.
    const asChild = blockChildEntitySchema.safeParse(entity);
    if (asChild.success) {
      authored.push(asChild.data);
    }
    // Entities that don't fit blockChildEntitySchema are silently
    // skipped — these can be surfaced as a warning in the UI but for
    // Phase 2.3 we match AutoCAD's "disallow unsupported in block"
    // stance.
  }

  const nextBlockDef = blockDefinitionSchema.parse({
    ...existing,
    entities: [...authored, ...preservedAttdefs],
  });

  return {
    ...document,
    blocks: {
      ...document.blocks,
      [blockName]: nextBlockDef,
    },
  };
}

// Handle `space/enter-block-edit`. Delegates to `enterBlockEditSpace`
// (drawing-model) for the snapshot + synthetic space creation, then
// hydrates the block-edit space's entities from the target
// BlockDefinition so the regular reducer pipeline sees the geometry
// during the session.
function reduceEnterBlockEdit(
  document: DrawingDocument,
  blockName: string,
  createIfMissing: boolean,
): DrawingDocument {
  const next = enterBlockEditSpace(document, blockName, { createIfMissing });
  return hydrateBlockEditSpace(next);
}

// Handle `space/exit-block-edit`. For action="save" the block-edit
// space is first mirrored back to the BlockDefinition so the saved
// state includes the latest authored geometry, then the synthetic
// space is dropped and the previous active space restored. For
// action="discard" the drawing-model helper restores the pre-BEDIT
// block snapshot and drops the space.
function reduceExitBlockEdit(
  document: DrawingDocument,
  action: "save" | "discard",
): DrawingDocument {
  const synced =
    action === "save" ? syncBlockEditSpaceToDefinition(document) : document;
  return exitBlockEditSpace(synced, action);
}

// Handle `space/save-block-edit` (BSAVE). Mirror block-edit space
// entities back into the BlockDefinition and refresh the snapshot so
// a subsequent discard rolls back only post-save changes. The
// synthetic space stays active; no space switch occurs.
function reduceSaveBlockEdit(document: DrawingDocument): DrawingDocument {
  const synced = syncBlockEditSpaceToDefinition(document);
  return refreshBlockEditSnapshot(synced);
}

// Handle `space/enter-block-test` (DYN-1.9 — TESTBLOCK). Before
// entering the preview we mirror any pending BEDIT edits back into
// the target BlockDefinition so the sandbox INSERT evaluates against
// the latest authored geometry — without this pre-sync the preview
// would render a stale blockDef.entities snapshot taken at BEDIT
// start.
function reduceEnterBlockTest(
  document: DrawingDocument,
  blockName: string,
): DrawingDocument {
  const synced = syncBlockEditSpaceToDefinition(document);
  return enterBlockTestSpace(synced, blockName);
}

// Handle `space/exit-block-test` (CLOSETESTBLOCK). Delegates to the
// drawing-model helper — no state needs to be mirrored back because
// the sandbox is non-authoritative.
function reduceExitBlockTest(document: DrawingDocument): DrawingDocument {
  return exitBlockTestSpace(document);
}

// ── REFEDIT (Phase FIN.1) ────────────────────────────────────────────
// Mirror BEDIT's pattern: the drawing-model helper handles the snapshot
// + synthetic-space creation; on exit save we mirror the working-set
// entities back into the BlockDefinition. The mirror back is performed
// inside `exitRefeditSpace` because the working set changes per session.
function reduceEnterRefedit(
  document: DrawingDocument,
  insertId: string,
): DrawingDocument {
  return enterRefeditSpace(document, insertId);
}

function reduceExitRefedit(
  document: DrawingDocument,
  action: "save" | "discard",
): DrawingDocument {
  return exitRefeditSpace(document, action);
}

function reduceRefeditAddToWorkingSet(
  document: DrawingDocument,
  entityIds: string[],
): DrawingDocument {
  return addToRefeditWorkingSet(document, entityIds);
}

function reduceRefeditRemoveFromWorkingSet(
  document: DrawingDocument,
  entityIds: string[],
): DrawingDocument {
  return removeFromRefeditWorkingSet(document, entityIds);
}

/**
 * Unified dispatcher for space/enter-block-edit /
 * space/exit-block-edit / space/save-block-edit /
 * space/enter-block-test / space/exit-block-test /
 * space/enter-refedit / space/exit-refedit /
 * space/refedit-add-to-working-set / space/refedit-remove-from-working-set.
 * Centralises the error handling ("Block missing and createIfMissing=false"
 * is a silent no-op so App.tsx can surface a toast without the reducer
 * throwing) and the re-entry guard.
 */
export function dispatchBlockEditCommand(
  document: DrawingDocument,
  command:
    | {
        type: "space/enter-block-edit";
        blockName: string;
        createIfMissing: boolean;
      }
    | { type: "space/exit-block-edit"; action: "save" | "discard" }
    | { type: "space/save-block-edit" }
    | { type: "space/enter-block-test"; blockName: string }
    | { type: "space/exit-block-test" }
    | { type: "space/enter-refedit"; insertId: string }
    | { type: "space/exit-refedit"; action: "save" | "discard" }
    | { type: "space/refedit-add-to-working-set"; entityIds: string[] }
    | { type: "space/refedit-remove-from-working-set"; entityIds: string[] },
): DrawingDocument {
  switch (command.type) {
    case "space/enter-block-edit": {
      const existing = document.spaces.find((s) => s.kind === "block-edit");
      if (existing && existing.blockEditName === command.blockName) {
        return document;
      }
      try {
        return reduceEnterBlockEdit(
          document,
          command.blockName,
          command.createIfMissing,
        );
      } catch {
        return document;
      }
    }
    case "space/exit-block-edit":
      return reduceExitBlockEdit(document, command.action);
    case "space/save-block-edit":
      return reduceSaveBlockEdit(document);
    case "space/enter-block-test": {
      // Re-enter with the same block is a no-op (idempotent). Entering
      // with a different block replaces the active sandbox.
      const existing = document.spaces.find((s) => s.kind === "block-test");
      if (existing && existing.blockEditName === command.blockName) {
        return document;
      }
      try {
        return reduceEnterBlockTest(document, command.blockName);
      } catch {
        return document;
      }
    }
    case "space/exit-block-test":
      return reduceExitBlockTest(document);
    case "space/enter-refedit": {
      // Re-enter with the same insert is idempotent.
      const existing = document.spaces.find((s) => s.kind === "refedit");
      if (existing && existing.refeditSourceInsertId === command.insertId) {
        return document;
      }
      try {
        return reduceEnterRefedit(document, command.insertId);
      } catch {
        return document;
      }
    }
    case "space/exit-refedit":
      return reduceExitRefedit(document, command.action);
    case "space/refedit-add-to-working-set":
      return reduceRefeditAddToWorkingSet(document, command.entityIds);
    case "space/refedit-remove-from-working-set":
      return reduceRefeditRemoveFromWorkingSet(document, command.entityIds);
    default:
      return document;
  }
}
